import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Routes, Route } from "react-router-dom";
import { UserProvider } from './context/UserContext';

// Load essential pages normally
import InaugurationPage from "./pages/Inaugration/inaugrationPage";
import LoginPage from './pages/Login/LoginPage';
import LandingPage from './pages/LandingPage/LandingPage';
import ForbiddenPage from './pages/ForbiddenPage';
import Loading from "./components/Loader";
import { DisplayUserList } from "./components/CollegeAdminComponents/DIsplayUserList";
import { EventRegisteredUsers } from "./components/CollegeAdminComponents/EventRegisteredUsers";
import { SelectParticipantPage } from "./pages/AdminDashboardPage/SelectParticipantPage";
import {AdmitParticipantPage} from "./pages/AdmissionOfficerDashboard/AdmitParticipantPage";
import DeveloperPage from "./pages/DeveloperPage/DeveloperPage.jsx";
import ScheduleTable from "./pages/LandingPage/Schedule.jsx";

const ForgotPasswordPage = lazy(() => import('./pages/Forgot Password/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/Reset Password/ResetPasswordPage'));
const EmailConfirmationPage = lazy(() => import('./pages/Signup/EmailConfirmationPage'));
const EmailVerification = lazy(() => import('./pages/Signup/EmailVerification'));
const ResetResponse = lazy(() => import('./pages/Forgot Password/ResetResponse'));
const PasswordResetSuccess = lazy(() => import('./pages/Forgot Password/ResetSuccess'));
const PasswordResetFailed = lazy(() => import('./pages/Forgot Password/ResetFailure'));
const Downloads = lazy(() => import('./pages/LandingPage/Downloads'))

// Lazy load other pages
const CommonLayout = lazy(() => import('./components/layout-components/CommonLayout'));
const Dashboard = lazy(() => import('./pages/DashboardPage/Dashboard'));
const IdeaSection = lazy(() => import('./components/sub-components/dashboard/IdeaSection'));
const ProjectSection = lazy(() => import('./components/sub-components/dashboard/ProjectSection'));
const EventPage = lazy(() => import('./pages/AdminDashboardPage/EventPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboardPage/Dashboard'));
const AdminList = lazy(() => import('./components/AdminComponents/admins'));
const StudentList = lazy(() => import('./components/AdminComponents/students'));
const FacultyList = lazy(() => import('./components/AdminComponents/faculty'));
const AlumniList = lazy(() => import('./components/AdminComponents/alumni'));
const OrganizerList = lazy(() => import('./components/AdminComponents/organizers'));
const ReviewerList = lazy(() => import('./components/AdminComponents/reviewer'));
const SpeakerList = lazy(() => import('./components/AdminComponents/speakers'));
const SponsorList = lazy(() => import('./components/AdminComponents/sponsors'));
const IndustryList = lazy(() => import('./components/AdminComponents/industry'));
const IdeasPage = lazy(() => import('./pages/AdminDashboardPage/IdeasPage'));
const ProductsPage = lazy(() => import('./pages/AdminDashboardPage/productsPage'));
const ReqPage = lazy(() => import('./pages/AdminDashboardPage/RequirementsPage'));
const IndustryVerification = lazy( () => import("./pages/AdminDashboardPage/industryVerification"));
const PreConclavePage = lazy(() => import('./pages/PreConclavePage/PreConclavePage'));
const CollegeAdminDashboard = lazy(() => import('./pages/CollegeAdminDashboard/Dashboard'));
const CollegeAdminNavBar = lazy(() => import('./components/CollegeAdminComponents/NavBar'));
const PreConclaveEvents = lazy(() => import('./pages/CollegeAdminDashboard/PreConclaveEvents'));
const DisplayMainEvents = lazy(() => import('./pages/CollegeAdminDashboard/DisplayMainEvent'));
const NotificationPage = lazy(() => import('./components/NotificationPage'));
const EventSection = lazy(() => import('./components/sub-components/dashboard/EventSection'));
const RequirementSection = lazy(() => import('./components/sub-components/dashboard/RequirementSection'));
const Profile = lazy(() => import('./pages/DashboardPage/Profile')); // Lazy load Profile
const NavBar = lazy(() => import('./pages/AdminDashboardPage/NavBar[1]'));
const  ChallengeSection = lazy( () => import('./components/sub-components/dashboard/ChallengeSection'));
const ChallengePage = lazy( () => import("./pages/AdminDashboardPage/ChallengePage"));
const AdmissionOfficerPage = lazy( () => import("./pages/AdmissionOfficerDashboard/AdmissionOfficerPage"));
const AdmissionOfficerNavBar = lazy( () => import("./components/AdmissionNavbar.jsx"));
const AdmissionByEvent = lazy( () => import("./pages/AdmissionOfficerDashboard/AdmissionByEvent.jsx"));
const SpotAdmissionPage  = lazy( () => import("./pages/AdmissionOfficerDashboard/SpotAdmissionPage.jsx"));
const AdmissionByEventRegistration  = lazy( () => import("./pages/AdmissionOfficerDashboard/AdmissionByEventRegistration.jsx"));

const router = createBrowserRouter([
  {
    path: "/inaugration",
    element: <InaugurationPage />,
  },
  {
    path: "/schedule",
    element: <ScheduleTable />,
  },
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/developers",
    element: <DeveloperPage />
  },
  {
    path: "/downloads",
    element: (
      <Suspense fallback={<Loading/>}>
        <Downloads />
      </Suspense>
    )
  },
  {
    path: "/pre-conclave/:selectedSDG",
    element: (
      <Suspense fallback={<Loading/>}>
        <PreConclavePage />
      </Suspense>
    )
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: (
      <Suspense fallback={<Loading/>}>
        <ForgotPasswordPage />
      </Suspense>
    )
  },
  {
    path: "/reset-password",
    element: (
      <Suspense fallback={<Loading/>}>
        <ResetPasswordPage />
      </Suspense>
    )
  },
  {
    path: "/reset-password-success",
    element: (
      <Suspense fallback={<Loading/>}>
        <PasswordResetSuccess />
      </Suspense>
    )
  },
  {
    path: "/reset-password-failure",
    element: (
      <Suspense fallback={<Loading/>}>
        <PasswordResetFailed />
      </Suspense>
    )
  },
  {
    path: "/confirm-email",
    element: (
      <Suspense fallback={<Loading/>}>
        <EmailConfirmationPage />
      </Suspense>
    )
  },
  {
    path: "/verification",
    element: (
      <Suspense fallback={<Loading/>}>
        <EmailVerification />
      </Suspense>
    )
  },
  {
    path: "/reset-email-response",
     element: (
      <Suspense fallback={<Loading/>}>
        <ResetResponse />
      </Suspense>
    )
  },
  {
    path: "/forbidden",
    element: <ForbiddenPage />,
  },
  {
    path: "/dashboard/*",
    element: (
      <Suspense fallback={<Loading/>}>
        <CommonLayout>
          <UserProvider>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="ideas" element={<IdeaSection />} />
              <Route path="products" element={<ProjectSection />} />
              <Route path="notifications" element={<NotificationPage />} />
              <Route path="profile" element={<Profile />} /> {/* Profile Route */}
              <Route path="upcoming-events" element={<EventSection />} />
              <Route path="requirements" element={<RequirementSection />} />
              <Route path="challenges" element={<ChallengeSection />} />
            </Routes>
          </UserProvider>
        </CommonLayout>
      </Suspense>
    )
  },
  {
    path: "/admin/*",
    element: (
      <Suspense fallback={<Loading/>}>
        <NavBar />
        <Routes>
          <Route path="/" element={<EventPage />} />
          <Route path="particapants" element={<AdminDashboard />} />
          <Route path="reviewer" element={<ReviewerList />} />
          <Route path="admins" element={<AdminList />} />
          <Route path="students" element={<StudentList />} />
          <Route path="faculty" element={<FacultyList />} />
          <Route path="alumni" element={<AlumniList />} />
          <Route path="organizers" element={<OrganizerList />} />
          <Route path="speakers" element={<SpeakerList />} />
          <Route path="sponsors" element={<SponsorList />} />
          <Route path="industry" element={<IndustryList />} />
          <Route path="ideas" element={<IdeasPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="requirements" element={<ReqPage />} />
          <Route path="challenges" element={<ChallengePage />} />
          <Route path="selectparticipant" element={<SelectParticipantPage />} />
          <Route path="industry-verification" element={<IndustryVerification />} />
        </Routes>
      </Suspense>
    )
  },
  {
    path: "/college-admin/*",
    element: (
      <Suspense fallback={<Loading/>}>
        <CollegeAdminNavBar />
        <Routes>
          <Route path="/" element={<CollegeAdminDashboard />} />
          <Route path="pre-conclave" element={<PreConclaveEvents />} />
          <Route path="add-participant" element={<DisplayMainEvents />} />
          <Route path="register-participant" element={<EventRegisteredUsers/>} />
        </Routes>
      </Suspense>
    )
  },
  {
    path: "/admission-officer/*",
    element: (
      <Suspense fallback={<Loading/>}>
        <AdmissionOfficerNavBar />
        <Routes>
          <Route path="/" element={<AdmitParticipantPage />} />
          <Route path="admission-by-event" element={<AdmissionByEvent />} />
          {/* <Route path="admit-participant" element={<AdmitParticipantPage />} /> */}
          <Route path="spot-admission" element={<SpotAdmissionPage />} />
          <Route path="register-participant" element={<AdmissionByEventRegistration />} />
        </Routes>
      </Suspense>
    )
  }
]);

export default function App() {
  return (
    <RouterProvider router={router} />  
  );
}
